import React, { useState, useEffect } from "react";
import { View, StyleSheet, ActivityIndicator } from "react-native";
import CustomText from "../../components/common/general/CustomText/CustomText";
import CustomButton from "../../components/common/general/CustomButton/CustomButton";
import ActivityCardList from "../../components/customCoursework/ActivityCardList/ActivityCardList";
import { EditTaskGrey, SparkleBlue } from "../../components/svgs/common";
import { PlusCircle } from "../../components/svgIcons";
import CustomSpacing from "../../components/common/layout/CustomSpacing/CustomSpacing";
import MoveActivityModal from "../../components/customCoursework/MoveActivityModal/MoveActivityModal";
import GenerateLessonPlanModal from "../../components/customCoursework/GenerateLessonPlanModal/GenerateLessonPlanModal";
import { useAppConfig } from "../../AppConfigProvider";
import { Trash } from "@phosphor-icons/react";
import { ArrowClockwise } from "@phosphor-icons/react/dist/csr/ArrowClockwise";
import { DownloadSimple } from "@phosphor-icons/react/dist/csr/DownloadSimple";
import Tooltip from "../../components/common/tooltip/Tooltip";

const BUTTON_HEIGHT = 32;

const ModuleContainer = ({
  module,
  allModules,
  projects = [],
  onEditModule,
  onDeleteModule,
  onOpenAddActivity,
  onItemOptionsPress,
  onDragEnd,
  onMoveActivity,
  onGenerateLessonPlan,
  onDownloadLessonPlan,
  lessonPlanStatus,
  lessonPlanError,
  style = {},
}) => {
  const [isMoveModalVisible, setIsMoveModalVisible] = useState(false);
  const [isLessonPlanModalVisible, setIsLessonPlanModalVisible] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [localModuleData, setLocalModuleData] = useState(module);
  const [isGeneratingLessonPlan, setIsGeneratingLessonPlan] = useState(false);
  const [localLessonPlanStatus, setLocalLessonPlanStatus] = useState({
    isGenerating: false,
    hasLessonPlan: false,
  });
  const { primaryColor } = useAppConfig();

  useEffect(() => {
    setLocalModuleData(module);
  }, [module]);

  useEffect(() => {
    if (lessonPlanStatus) {
      setLocalLessonPlanStatus(lessonPlanStatus);
    }
  }, [lessonPlanStatus]);

  const hasActivities =
    localModuleData?.aiTutors?.length > 0 ||
    localModuleData?.pages?.length > 0 ||
    localModuleData?.axioResources?.length > 0 ||
    localModuleData?.assignments?.length > 0 ||
    (localModuleData?.assessment && localModuleData?.assessment?.aiTutors?.length > 0) ||
    localModuleData?.reviews?.length > 0 ||
    localModuleData?.projects?.length > 0;

  const handleActivityOptionsPress = (action, activity) => {
    if (action === "move") {
      setSelectedActivity(activity);
      setIsMoveModalVisible(true);
    } else {
      // Pass other actions to the parent handler
      onItemOptionsPress(action, activity);
    }
  };

  const handleMoveActivity = async (activityId, sourceModuleId, targetModuleId, activityType) => {
    try {
      if (onMoveActivity) {
        await onMoveActivity(activityId, sourceModuleId, targetModuleId, activityType);
      }

      if (activityType === "Interactive Learning Experience") {
        setLocalModuleData((prev) => ({
          ...prev,
          aiTutors: prev.aiTutors?.filter((item) => item.id !== activityId) || [],
          resourceOrder: prev.resourceOrder?.filter((id) => id !== activityId) || [],
        }));
      } else if (activityType === "Resource") {
        setLocalModuleData((prev) => ({
          ...prev,
          axioResources: prev.axioResources?.filter((item) => item.id !== activityId) || [],
          resourceOrder: prev.resourceOrder?.filter((id) => id !== activityId) || [],
        }));
      } else if (activityType === "Assignment") {
        setLocalModuleData((prev) => ({
          ...prev,
          assignments: prev.assignments?.filter((item) => item.id !== activityId) || [],
          resourceOrder: prev.resourceOrder?.filter((id) => id !== activityId) || [],
        }));
      } else if (activityType === "Review") {
        setLocalModuleData((prev) => ({
          ...prev,
          reviews: prev.reviews?.filter((item) => item.id !== activityId) || [],
          resourceOrder: prev.resourceOrder?.filter((id) => id !== activityId) || [],
        }));
      } else if (activityType === "Project") {
        setLocalModuleData((prev) => ({
          ...prev,
          projects: prev.projects?.filter((item) => item.id !== activityId) || [],
          resourceOrder: prev.resourceOrder?.filter((id) => id !== activityId) || [],
        }));
      }

      setIsMoveModalVisible(false);
      setSelectedActivity(null);
    } catch (error) {
      console.error("Error during activity move:", error);
    }
  };

  const handleCloseMoveModal = () => {
    setIsMoveModalVisible(false);
    setSelectedActivity(null);
  };

  const handleOpenLessonPlanModal = () => {
    if (hasActivities) {
      setIsLessonPlanModalVisible(true);
    }
  };

  const handleCloseLessonPlanModal = () => {
    if (!isGeneratingLessonPlan) {
      setIsLessonPlanModalVisible(false);
    }
  };

  const handleGenerateLessonPlan = async (selectedSections) => {
    try {
      setIsGeneratingLessonPlan(true);

      if (onGenerateLessonPlan) {
        await onGenerateLessonPlan(localModuleData.id, selectedSections);
      }

      setIsLessonPlanModalVisible(false);
    } catch (error) {
      console.error("Error generating lesson plan:", error);
    } finally {
      setIsGeneratingLessonPlan(false);
    }
  };

  const handleDownloadLessonPlan = async () => {
    try {
      if (onDownloadLessonPlan) {
        await onDownloadLessonPlan(localModuleData.id);
      }
    } catch (error) {
      console.error("Error downloading lesson plan:", error);
    }
  };

  // Render lesson plan button based on status and errors
  const renderLessonPlanButton = () => {
    if (localLessonPlanStatus.isGenerating) {
      return (
        <CustomButton
          leftIcon={<ActivityIndicator size="small" color={primaryColor} />}
          text="Generating..."
          size="s"
          styleType="primaryTransparentStroke"
          disabled={true}
          onPress={() => {}}
          textStyle={{ fontSize: 14, fontWeight: 700 }}
          style={styles.generalButtonStyle}
        />
      );
    }

    if (localLessonPlanStatus.hasLessonPlan) {
      return (
        <View style={{ flexDirection: "row", gap: 8 }}>
          <CustomButton
            leftIcon={<DownloadSimple size={16} weight="bold" color={primaryColor} />}
            text="Download Lesson Plan"
            size="s"
            styleType="primaryTransparentStroke"
            textStyle={{ fontSize: 14, fontWeight: 700 }}
            onPress={handleDownloadLessonPlan}
            style={styles.generalButtonStyle}
          />
          <Tooltip content="Regenerate Lesson Plan" horizontalOffset={-90}>
            <CustomButton
              // text="Retry"
              leftIcon={<ArrowClockwise size={16} color={primaryColor} weight="bold" />}
              size="s"
              styleType="primaryTransparentStroke"
              textStyle={{ fontSize: 14, fontWeight: 700 }}
              onPress={handleOpenLessonPlanModal}
              disabled={!hasActivities}
              style={[styles.generalButtonStyle, !hasActivities && { opacity: 0.5 }]}
            />
          </Tooltip>
        </View>
      );
    }

    return (
      <View style={{ flexDirection: "column", alignItems: "flex-start" }}>
        <CustomButton
          leftIcon={<SparkleBlue width={16} height={16} />}
          text="Generate Lesson Plan"
          size="s"
          styleType="primaryTransparentStroke"
          textStyle={{ fontSize: 14, fontWeight: 700 }}
          onPress={handleOpenLessonPlanModal}
          disabled={isGeneratingLessonPlan || !hasActivities}
          style={[styles.generalButtonStyle, (!hasActivities || isGeneratingLessonPlan) && { opacity: 0.5 }]}
        />
        {lessonPlanError && (
          <CustomText
            text={lessonPlanError}
            size="xs"
            style={{ color: "#DC2626", marginTop: 4 }}
            useTranslationText={false}
          />
        )}
      </View>
    );
  };

  return (
    <View style={[styles.container, style]}>
      <View style={styles.moduleHeader}>
        <View style={styles.titleContainer}>
          <View style={styles.titleRow}>
            <View style={styles.titleRowLeft}>
              <CustomText
                text={localModuleData?.title}
                weight="bold"
                size="xl"
                useTranslationText={false}
                style={{ color: "#101828" }}
              />
              <CustomButton
                leftIcon={<EditTaskGrey width={16} height={16} />}
                aria-label="Edit Module"
                size="s"
                onPress={() => onEditModule(localModuleData)}
                style={{
                  backgroundColor: "transparent",
                  elevation: 0,
                  shadowColor: "#fff",
                  shadowOffset: { width: 0, height: 0 },
                  shadowOpacity: 0,
                  shadowRadius: 0,
                  boxShadow: "none",
                }}
              />
            </View>
            <View style={styles.titleRowRight}>
              <CustomButton
                leftIcon={<Trash color={primaryColor} size={16} weight="bold" />}
                text="Delete Module"
                size="s"
                styleType="primaryTransparentStroke"
                textStyle={{ fontSize: 14, fontWeight: 700 }}
                onPress={() => onDeleteModule(localModuleData)}
                style={styles.generalButtonStyle}
              />
            </View>
          </View>
          <CustomSpacing type="vertical" size="m" />
        </View>
      </View>

      <View style={styles.activitiesSection}>
        <View style={styles.activitiesHeader}>
          <View style={styles.activitiesHeaderRow}>
            <CustomText text="Activities" weight="ultraBold" size="m" style={{ color: "#182230" }} />
            <View style={styles.activitiesHeaderButtons}>
              {renderLessonPlanButton()}
              <CustomButton
                leftIcon={<PlusCircle width={16} height={16} />}
                text="Activity"
                size="s"
                styleType="primaryTransparentStroke"
                textStyle={{ fontSize: 14, fontWeight: 700 }}
                onPress={onOpenAddActivity}
                style={styles.generalButtonStyle}
              />
            </View>
          </View>
        </View>

        {hasActivities ? (
          <View style={styles.activityListContainer}>
            <ActivityCardList
              module={localModuleData}
              onItemOptionsPress={(action, item) => handleActivityOptionsPress(action, item)}
              style={styles.activityList}
              onDragEnd={onDragEnd}
            />
          </View>
        ) : (
          <View style={styles.noActivitiesContainer}>
            <CustomText
              text="This module doesn't have any Activities yet"
              useTranslationText={false}
              size="m"
              style={styles.noActivitiesText}
              weight="normal"
            />
          </View>
        )}
      </View>

      {selectedActivity && (
        <MoveActivityModal
          visible={isMoveModalVisible}
          onClose={handleCloseMoveModal}
          modules={allModules}
          currentModuleId={localModuleData.id}
          activityId={selectedActivity.id}
          activityType={selectedActivity.type}
          onMoveActivity={handleMoveActivity}
        />
      )}

      <GenerateLessonPlanModal
        visible={isLessonPlanModalVisible}
        onClose={handleCloseLessonPlanModal}
        onGenerate={handleGenerateLessonPlan}
        isGenerating={isGeneratingLessonPlan}
        moduleTitle={localModuleData?.title}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#FFFFFF",
  },
  moduleHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginHorizontal: 24,
    paddingTop: 30,
    paddingBottom: 20,
    borderBottomWidth: 1,
    borderBottomColor: "#EAECF0",
  },
  titleContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    flex: 1,
  },
  titleRowTitleContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 8,
  },
  titleRowLeft: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 8,
    flex: 1,
  },
  titleRowRight: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
  },
  titleRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
  },
  activitiesSection: {
    flex: 1,
  },
  activitiesHeader: {
    flexDirection: "column",
    justifyContent: "flex-start",
    paddingHorizontal: 24,
    paddingVertical: 20,
  },
  activitiesHeaderRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  activitiesHeaderButtons: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  activityListContainer: {
    paddingHorizontal: 20,
  },
  activityList: {
    flex: 1,
  },
  noActivitiesContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#FCFCFD",
    marginHorizontal: 20,
    borderRadius: 10,
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderWidth: 1,
    borderColor: "#EAECF0",
    minHeight: 248,
  },
  noActivitiesText: {
    color: "#344054",
  },
  generalButtonStyle: {
    padding: 0,
    paddingHorizontal: 12,
    paddingVertical: 8,
    height: BUTTON_HEIGHT,
    borderColor: "#EAECF0",
  },
});

export default ModuleContainer;
